import React, { useState, useCallback } from 'react';
import { X } from 'lucide-react';
import DoctorDetailsModal from './DoctorDetailsModal';
import ServiceSelectionModal from './ServiceSelectionModal';
import BookingFormModal from './BookingFormModal';
import { services } from '../data/mock';
import { supabase } from '../lib/supabase';
import { sendTelegramNotification } from '../lib/telegram';

const DoctorBookingFlow = ({ doctor, onClose, isOpen }) => {
  const [currentStep, setCurrentStep] = useState('doctor-details');
  const [selectedDoctor, setSelectedDoctor] = useState(doctor);
  const [selectedService, setSelectedService] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    date: null,
    time: ''
  });
  const hasAnimated = React.useRef(false);

  // Animate modal opening using Web Animations API
  React.useEffect(() => {
    if (!hasAnimated.current) {
      hasAnimated.current = true;
      
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const modalContent = document.querySelector('.modal-content');
        if (modalContent) {
          modalContent.animate([
            { opacity: 0, transform: 'translateY(30px)' },
            { opacity: 1, transform: 'translateY(0)' }
          ], {
            duration: 400,
            easing: 'ease',
            fill: 'forwards'
          });
        }
      }, 0);
    }
  }, []);

  const handleNextStep = useCallback((step) => {
    setCurrentStep(step);
    
    // Animate new step
    setTimeout(() => {
      const modalContent = document.querySelector('.modal-content');
      if (modalContent) {
        modalContent.animate([
          { opacity: 0, transform: 'translateY(20px)' },
          { opacity: 1, transform: 'translateY(0)' }
        ], {
          duration: 300,
          easing: 'ease',
          fill: 'forwards'
        });
      }
    }, 0);
  }, []);

  const handlePreviousStep = useCallback(() => {
    if (currentStep === 'service-selection') {
      setCurrentStep('doctor-details');
    } else if (currentStep === 'booking-form') {
      setCurrentStep('service-selection');
    }
    
    // Animate previous step
    setTimeout(() => {
      const modalContent = document.querySelector('.modal-content');
      if (modalContent) {
        modalContent.animate([
          { opacity: 0, transform: 'translateY(20px)' },
          { opacity: 1, transform: 'translateY(0)' }
        ], {
          duration: 300,
          easing: 'ease',
          fill: 'forwards'
        });
      }
    }, 0);
  }, [currentStep]);

  const handleClose = useCallback(() => {
    const modalContent = document.querySelector('.modal-content');
    const modalOverlay = document.querySelector('.modal-overlay');
    
    if (modalContent && modalOverlay) {
      // Animate closing using Web Animations API
      modalContent.animate([
        { opacity: 1, transform: 'translateY(0)' },
        { opacity: 0, transform: 'translateY(30px)' }
      ], {
        duration: 300,
        easing: 'ease',
        fill: 'forwards'
      });
      
      modalOverlay.animate([
        { opacity: 1 },
        { opacity: 0 }
      ], {
        duration: 300,
        easing: 'ease',
        fill: 'forwards'
      });
    }
    
    setTimeout(() => {
      setCurrentStep('doctor-details');
      setSelectedService(null);
      setFormData({
        name: '',
        phone: '',
        email: '',
        date: null,
        time: ''
      });
      onClose();
    }, 300);
  }, [onClose]);

  const handleServiceSelect = useCallback((service) => {
    setSelectedService(service);
    setCurrentStep('booking-form');
  }, []);

  const handleFormSubmit = useCallback(async (data) => {
    try {
      // Перевірка даних
      if (!data.name || !data.phone || !data.date || !data.time) {
        alert('Будь ласка, заповніть всі обов\'язкові поля');
        return;
      }

      // Перевірка Supabase
      if (!supabase) {
        alert('Помилка підключення до бази даних. Перезапустіть сервер.');
        return;
      }
      
      // Використовуємо локальну дату
      const year = data.date.getFullYear();
      const month = String(data.date.getMonth() + 1).padStart(2, '0');
      const day = String(data.date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      const bookingData = {
        doctor_name: `${selectedDoctor.name} (${selectedDoctor.position})`,
        service_title: selectedService.title,
        patient_name: data.name,
        patient_phone: data.phone,
        patient_email: data.email || null,
        booking_date: dateStr,
        booking_time: data.time,
        status: 'pending',
        created_at: new Date().toISOString()
      };

      const { data: savedBooking, error } = await supabase
        .from('bookings')
        .insert([bookingData])
        .select()
        .single();

      if (error) {
        alert(`Помилка збереження: ${error.message}`);
        throw error;
      }
      
      // Відправити нотифікацію в Telegram
      sendTelegramNotification(savedBooking).then(result => {
        if (!result.success) {
          console.error('Failed to send Telegram notification:', result.error);
        }
      }).catch(err => {
        console.error('Error in Telegram notification:', err);
      });
      
      // Show success message and close after 3 seconds
      setCurrentStep('success');
      setTimeout(() => {
        handleClose();
      }, 3000);
    } catch (error) {
      console.error('❌ Error saving booking:', error);
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      alert(`Помилка при збереженні запису: ${error.message}\n\nПеревірте консоль (F12) для деталей.`);
    }
  }, [selectedDoctor, selectedService, handleClose]);

  if (!isOpen) return null;

  return (
    <>
      {currentStep === 'doctor-details' && (
        <DoctorDetailsModal
          doctor={selectedDoctor}
          onNext={() => handleNextStep('service-selection')}
          onClose={handleClose}
        />
      )}

      {currentStep === 'service-selection' && (
        <ServiceSelectionModal
          services={services}
          onServiceSelect={handleServiceSelect}
          onBack={handlePreviousStep}
          onClose={handleClose}
        />
      )}

      {currentStep === 'booking-form' && (
        <BookingFormModal
          doctor={selectedDoctor}
          service={selectedService}
          onBack={handlePreviousStep}
          onClose={handleClose}
          onSubmit={handleFormSubmit}
        />
      )}

      {currentStep === 'success' && (
        <div className="modal-overlay" onClick={handleClose}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="modal-close" 
              onClick={handleClose} 
              type="button"
              aria-label="Закрити вікно"
            >
              <X size={24} />
            </button>
            <div className="success-message">
              <div className="success-icon">✓</div>
              <h2>Дякуємо!</h2>
              <p>Ваш запис прийнято</p>
              <p className="success-details">
                Ми зв'яжемося з вами найближчим часом для підтвердження
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DoctorBookingFlow;
